import config from '../config/environment';

export interface LeaderboardEntry {
  _id: string;
  userId: {
    _id: string;
    username: string;
    displayName: string;
    avatar?: string;
    level: number;
  };
  gameType: 'overall' | 'kadi-ni-tidi' | 'teen-patti' | 'rummy';
  period: 'daily' | 'weekly' | 'monthly' | 'all-time';
  rank: number;
  points: number;
  gamesPlayed: number;
  wins: number;
  losses: number;
  draws: number;
  winRate: number;
  currentWinStreak: number;
  maxWinStreak: number;
  currentLossStreak: number;
  lastGameAt?: string;
  updatedAt: string;
  createdAt: string;
}

export interface UserRankInfo {
  entry: LeaderboardEntry | null;
  rank: number;
  totalPlayers: number;
}

export interface LeaderboardStats {
  totalPlayers: number;
  activeToday: number;
  activeThisWeek: number;
  activeThisMonth: number;
  topGameType: string;
}

class LeaderboardApi {
  private baseUrl: string;

  constructor() {
    this.baseUrl = config.api.baseUrl;
  }

  private async fetchWithAuth<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const token = localStorage.getItem('authToken');
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options?.headers as Record<string, string> || {}),
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Request failed' }));
      throw new Error(error.message || `HTTP ${response.status}`);
    }

    const data = await response.json();
    return data;
  }

  /**
   * Get top players for a specific game type and period
   */
  async getTopPlayers(
    gameType: 'overall' | 'kadi-ni-tidi' | 'teen-patti' | 'rummy',
    period: 'daily' | 'weekly' | 'monthly' | 'all-time',
    limit: number = 100
  ): Promise<LeaderboardEntry[]> {
    try {
      const response = await this.fetchWithAuth<{
        success: boolean;
        data: { entries: LeaderboardEntry[] };
      }>(`/api/leaderboard/${gameType}/${period}?limit=${limit}`);

      return response.data.entries;
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      throw error;
    }
  }

  /**
   * Get a specific user's rank for a game type and period
   */
  async getUserRank(
    userId: string,
    gameType: 'overall' | 'kadi-ni-tidi' | 'teen-patti' | 'rummy',
    period: 'daily' | 'weekly' | 'monthly' | 'all-time'
  ): Promise<UserRankInfo> {
    try {
      const response = await this.fetchWithAuth<{
        success: boolean;
        data: UserRankInfo;
      }>(`/api/leaderboard/${gameType}/${period}/user/${userId}`);

      return response.data;
    } catch (error) {
      console.error('Error fetching user rank:', error);
      // Return empty result if user not found
      return {
        entry: null,
        rank: 0,
        totalPlayers: 0,
      };
    }
  }

  /**
   * Get all ranks for a user across all game types and periods
   */
  async getUserAllRanks(userId: string): Promise<{
    [gameType: string]: {
      [period: string]: UserRankInfo;
    };
  }> {
    try {
      const response = await this.fetchWithAuth<{
        success: boolean;
        data: {
          ranks: {
            [gameType: string]: {
              [period: string]: UserRankInfo;
            };
          };
        };
      }>(`/api/leaderboard/user/${userId}`);

      return response.data.ranks;
    } catch (error) {
      console.error('Error fetching user all ranks:', error);
      throw error;
    }
  }

  /**
   * Get leaderboard statistics
   */
  async getStats(): Promise<LeaderboardStats> {
    try {
      const response = await this.fetchWithAuth<{
        success: boolean;
        data: LeaderboardStats;
      }>('/api/leaderboard/stats');

      return response.data;
    } catch (error) {
      console.error('Error fetching leaderboard stats:', error);
      throw error;
    }
  }

  /**
   * Reset period leaderboards (admin only)
   */
  async resetLeaderboard(period: 'daily' | 'weekly' | 'monthly'): Promise<void> {
    try {
      await this.fetchWithAuth(`/api/leaderboard/reset/${period}`, {
        method: 'POST',
      });
    } catch (error) {
      console.error('Error resetting leaderboard:', error);
      throw error;
    }
  }
}

export const leaderboardApi = new LeaderboardApi();
export default leaderboardApi;
